import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { NoodleBriefQuestionGroupDto, NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';
import { UgcProductResponseDto } from '@/app/common/general-types/products.dto';
import {
  KanbanProgressResponseDto,
  ScreeningBriefResponseDto,
  ScreeningInquiriesResponseDto,
  ScreeningInquiryResponseDto,
  ScreeningKanBanStatusEnum,
  ScreeningListResponseDto,
  ScreeningResultsResponse,
} from '@/app/common/general-types/social-screening.dto';
import { NoodleService } from '@/app/services/noodle/noodle.service';
import { SocialScreeningService } from '@/app/services/social-screening/social-screening.service';
import {
  adaptNoodleFlags,
  collectFlaggedContent,
  detectAttribution,
  summariseCohort,
  summariseCreator,
} from '../../data/screening-results.adapter';
import {
  EMPTY_PROGRESS,
  FlaggedCohortContent,
  ScreeningCreatorProgress,
  ScreeningFlag,
  ScreeningKpis,
} from '../../data/screening-results.model';

/** How many inquiries to pull per page while building the cohort. */
const INQUIRY_PAGE_SIZE = 50;
/** Ceiling on cohort size, so a huge brief cannot fire unbounded per-creator requests. */
const MAX_COHORT = 200;

export type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/** A creator id paired with their results, or `undefined` when that one request failed. */
type ResultEntry = [string, ScreeningResultsResponse | undefined];

/** A creator on the brief, joined to whatever Noodle knows about their progress. */
export interface ScreeningCreatorRow {
  inquiryId: string;
  creatorUserId: string;
  noodleCreatorId?: string;
  name: string;
  initials: string;
  photoUrl?: string;
  instagramHandle?: string;
  tiktokHandle?: string;
  followers?: number;
  progress: ScreeningCreatorProgress;
  /** False when Noodle has no record for this creator — distinct from "nothing screened yet". */
  resultsAvailable: boolean;
}

/**
 * Loads everything the Manage Screening Brief surface needs and derives it once.
 *
 * Root-provided so the creator report can reuse a warm cache when the user arrives from the hub,
 * and can load cold when they deep-link straight to it. `load()` is idempotent per brief.
 */
@Injectable({ providedIn: 'root' })
export class ScreeningResultsService {

  private readonly socialScreening = inject(SocialScreeningService);
  private readonly noodle = inject(NoodleService);
  private readonly destroyRef = inject(DestroyRef);

  readonly briefId = signal<string | undefined>(undefined);

  readonly brief = signal<ScreeningBriefResponseDto | undefined>(undefined);
  readonly creatorList = signal<ScreeningListResponseDto | undefined>(undefined);
  readonly product = signal<UgcProductResponseDto | undefined>(undefined);
  readonly kanban = signal<KanbanProgressResponseDto | undefined>(undefined);

  readonly rules = signal<readonly NoodleQuestionDto[]>([]);
  /** Full Noodle group, kept so context can be saved without fetching the questions again. */
  readonly questionGroup = signal<NoodleBriefQuestionGroupDto | undefined>(undefined);
  /** Generalised questions reviewed in this session, including ones saved with no text. */
  readonly reviewedQuestionIds = signal<ReadonlySet<string>>(new Set());
  readonly brandSafetyRules = signal<readonly NoodleQuestionDto[]>([]);
  readonly deliverables = signal<readonly { id: string; summary: string; deliverable: string }[]>([]);
  readonly organisationId = signal<string | undefined>(undefined);
  readonly briefType = signal<string | undefined>(undefined);
  readonly briefIntId = signal<string | null>(null);

  readonly inquiries = signal<readonly ScreeningInquiryResponseDto[]>([]);
  private readonly noodleData = signal<unknown>(undefined);
  private readonly creatorResults = signal<ReadonlyMap<string, ScreeningResultsResponse>>(new Map());

  readonly briefState = signal<LoadState>('idle');
  readonly rulesState = signal<LoadState>('idle');
  readonly cohortState = signal<LoadState>('idle');
  /** True when the cohort hit `MAX_COHORT` and the KPI totals are therefore partial. */
  readonly cohortTruncated = signal<boolean>(false);
  /** Content a reviewer has approved locally, before Noodle sends the next results payload. */
  private readonly overriddenContentIds = signal<ReadonlySet<string>>(new Set());

  /** Flags resolved from the brief-level payload. Empty means "no detail", not "no problems". */
  readonly flags = computed<readonly ScreeningFlag[]>(() => {
    const flags = adaptNoodleFlags(this.noodleData(), this.rules());
    const overridden = this.overriddenContentIds();
    if (overridden.size === 0) {
      return flags;
    }
    return flags.filter((flag) => !overridden.has(flag.contentId));
  });

  /** Whether Noodle told us which rule each issue belongs to. */
  readonly attribution = computed(() => detectAttribution(this.flags()));

  /**
   * Posts with at least one issue, from the per-creator results.
   * This is the set behind the KPI when the brief payload has no per-rule rows.
   */
  readonly flaggedContent = computed<readonly FlaggedCohortContent[]>(() => {
    const overridden = this.overriddenContentIds();
    const posts = collectFlaggedContent(
      this.creators().map((creator) => ({
        name: creator.name,
        initials: creator.initials,
        handle: creatorHandle(creator),
        noodleCreatorId: creator.noodleCreatorId,
        results: creator.noodleCreatorId ? this.creatorResults().get(creator.noodleCreatorId) : undefined,
      })),
      this.flags(),
    );
    if (overridden.size === 0) {
      return posts;
    }
    return posts.filter((post) => !overridden.has(post.item.id));
  });

  readonly creators = computed<readonly ScreeningCreatorRow[]>(() => {
    const results = this.creatorResults();
    const flagsByCreator = this.flagsByCreator();
    const overridden = this.overriddenContentIds();
    return this.inquiries().map((inquiry) => {
      const noodleCreatorId = inquiry.noodleCreatorId ?? undefined;
      const result = noodleCreatorId ? results.get(noodleCreatorId) : undefined;
      const progress = result ? summariseCreator(result) : { ...EMPTY_PROGRESS };
      const resolvedFlags = noodleCreatorId ? flagsByCreator.get(noodleCreatorId) : undefined;
      return {
        inquiryId: inquiry.id,
        creatorUserId: inquiry.creatorUserId,
        noodleCreatorId,
        name: creatorName(inquiry),
        initials: initialsOf(creatorName(inquiry)),
        photoUrl: inquiry.creator?.photoURL,
        instagramHandle: inquiry.creator?.instaHandle ?? undefined,
        tiktokHandle: inquiry.creator?.tikTokHandle ?? undefined,
        followers: followerCount(inquiry),
        progress: {
          ...progress,
          flaggedContent: flaggedCountAfterOverrides(result, resolvedFlags, overridden, progress.flaggedContent),
        },
        resultsAvailable: !!result,
      };
    });
  });

  readonly flagsByCreator = computed<ReadonlyMap<string, ScreeningFlag[]>>(() => {
    const byCreator = new Map<string, ScreeningFlag[]>();
    for (const flag of this.flags()) {
      if (!flag.creatorId) {
        continue;
      }
      const bucket = byCreator.get(flag.creatorId) ?? [];
      bucket.push(flag);
      byCreator.set(flag.creatorId, bucket);
    }
    return byCreator;
  });

  readonly kpis = computed<ScreeningKpis>(() => {
    const progresses = this.creators().map((creator) => creator.progress);
    // Without per-flag detail the honest headline is how many items tripped a rule.
    const flagCount = this.flags().length
      || progresses.reduce((total, progress) => total + progress.flaggedContent, 0);
    return { ...summariseCohort(progresses, flagCount), creators: this.inquiries().length };
  });

  readonly ruleCounts = computed(() => {
    const rules = this.rules();
    const dos = rules.filter((rule) => rule.type === 'do').length;
    const subjective = rules.filter((rule) => rule.subjectiveType === 1).length;
    return {
      all: rules.length,
      dos,
      donts: rules.length - dos,
      subjective,
      objective: rules.length - subjective,
    };
  });

  /** Noodle only has rules once the brief has been processed. */
  private readonly rulesExpected = computed(() => {
    const status = this.kanban()?.status;
    return status !== ScreeningKanBanStatusEnum.Not_Ready && status !== ScreeningKanBanStatusEnum.Processing;
  });

  /** True when this question already has enrichment text, or was saved in this session. */
  hasQuestionContext(question: NoodleQuestionDto): boolean {
    if (this.reviewedQuestionIds().has(question.id)) {
      return true;
    }
    return (question.enrichments ?? []).some((item) => item.text.trim().length > 0);
  }

  /** Writes a saved enrichment pass back onto the rules the brief management tab reads. */
  applySavedQuestions(questions: readonly NoodleQuestionDto[]): void {
    this.rules.set(questions);
    const group = this.questionGroup();
    if (group) {
      this.questionGroup.set({
        ...group,
        questions: [...questions],
        questionsCount: questions.length,
      });
    }
    this.reviewedQuestionIds.update((current) => {
      const next = new Set(current);
      for (const question of questions) {
        if (question.generalised) {
          next.add(question.id);
        }
      }
      return next;
    });
  }

  /** Drops a post from the flagged list and the red-flag count without waiting for a reload. */
  markContentOverridden(contentId: string): void {
    const id = contentId.trim();
    if (!id) {
      return;
    }
    this.overriddenContentIds.update((current) => {
      if (current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.add(id);
      return next;
    });
  }

  /** Loads a brief and everything hanging off it. Re-calling with the same id is a no-op. */
  load(briefId: string, force = false): void {
    if (!force && this.briefId() === briefId && this.briefState() === 'ready') {
      return;
    }
    if (this.briefId() !== briefId) {
      this.resetFor(briefId);
    }

    this.briefState.set('loading');
    this.socialScreening
      .getScreeningBriefById(briefId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.brief.set(detail.screeningBrief);
          this.creatorList.set(detail.creatorList);
          this.product.set(detail.product);
          this.kanban.set(detail.currentKanbanProgress ?? detail.screeningBrief?.currentKanbanProgress);
          this.briefState.set('ready');
          if (this.rulesExpected()) {
            this.loadRules(briefId);
          } else {
            this.rulesState.set('ready');
          }
          this.loadCohort(briefId);
        },
        error: () => this.briefState.set('error'),
      });
  }

  /** Re-reads the cohort after creators have been added. */
  reloadCohort(): void {
    const briefId = this.briefId();
    if (briefId) {
      this.loadCohort(briefId);
    }
  }

  /** Per-creator results for the report screen; served from cache when the hub already loaded it. */
  resultsFor(noodleCreatorId: string): ScreeningResultsResponse | undefined {
    return this.creatorResults().get(noodleCreatorId);
  }

  private loadRules(briefId: string): void {
    this.rulesState.set('loading');
    this.noodle
      .getBriefQuestions(briefId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (group) => {
          this.questionGroup.set(group);
          this.rules.set(group?.questions ?? []);
          this.brandSafetyRules.set(group?.brandSafetyQuestions ?? []);
          this.deliverables.set(group?.conceptsDeliverables ?? []);
          this.organisationId.set(group?.organizationId);
          this.briefType.set(group?.briefType);
          this.briefIntId.set(group?.briefIntId ?? null);
          this.rulesState.set('ready');
        },
        error: () => this.rulesState.set('error'),
      });
  }

  private loadCohort(briefId: string): void {
    this.cohortState.set('loading');
    this.cohortTruncated.set(false);

    this.pageInquiries(briefId)
      .pipe(
        switchMap((inquiries) => {
          this.inquiries.set(inquiries);
          const ids = [...new Set(inquiries.map((inquiry) => inquiry.noodleCreatorId).filter(isPresent))];
          if (ids.length === 0) {
            return of([] as ResultEntry[]);
          }
          // One creator's results failing must not blank the whole cohort.
          return forkJoin(
            ids.map((id) =>
              this.socialScreening.getScreeningResults(id, briefId).pipe(
                map((result): ResultEntry => [id, result]),
                catchError(() => of<ResultEntry>([id, undefined])),
              ),
            ),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (entries) => {
          const resolved = new Map<string, ScreeningResultsResponse>();
          for (const [id, result] of entries) {
            if (result) {
              resolved.set(id, result);
            }
          }
          this.creatorResults.set(resolved);
          this.cohortState.set('ready');
        },
        error: () => this.cohortState.set('error'),
      });
  }

  /** Walks `get-inquiries-by-brief-id` until a short page or the cohort cap. */
  private pageInquiries(briefId: string): Observable<ScreeningInquiryResponseDto[]> {
    const collected: ScreeningInquiryResponseDto[] = [];

    const readPage = (skip: number): Observable<ScreeningInquiryResponseDto[]> =>
      this.socialScreening.getInquiriesByBriefId(briefId, INQUIRY_PAGE_SIZE, skip).pipe(
        catchError(() => of({ inquiries: [], noodleData: undefined } as ScreeningInquiriesResponseDto)),
        switchMap((page) => {
          if (page.noodleData !== undefined && page.noodleData !== null) {
            this.noodleData.set(page.noodleData);
          }
          const rows = page.inquiries ?? [];
          collected.push(...rows);
          const lastPage = rows.length < INQUIRY_PAGE_SIZE;
          if (collected.length >= MAX_COHORT) {
            this.cohortTruncated.set(!lastPage);
            return of(collected.slice(0, MAX_COHORT));
          }
          return lastPage ? of(collected) : readPage(skip + INQUIRY_PAGE_SIZE);
        }),
      );

    return readPage(0);
  }

  private resetFor(briefId: string): void {
    this.briefId.set(briefId);
    this.brief.set(undefined);
    this.creatorList.set(undefined);
    this.product.set(undefined);
    this.kanban.set(undefined);
    this.rules.set([]);
    this.questionGroup.set(undefined);
    this.reviewedQuestionIds.set(new Set());
    this.brandSafetyRules.set([]);
    this.deliverables.set([]);
    this.inquiries.set([]);
    this.noodleData.set(undefined);
    this.creatorResults.set(new Map());
    this.overriddenContentIds.set(new Set());
    this.rulesState.set('idle');
    this.cohortState.set('idle');
    this.cohortTruncated.set(false);
  }
}

function creatorHandle(creator: { instagramHandle?: string; tiktokHandle?: string }): string | undefined {
  const raw = creator.instagramHandle ?? creator.tiktokHandle;
  const trimmed = raw?.trim().replace(/^@/, '') ?? '';
  return trimmed ? `@${trimmed}` : undefined;
}

function isPresent(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function countDistinct(flags: readonly ScreeningFlag[]): number {
  return new Set(flags.map((flag) => flag.contentId)).size;
}

/**
 * Pieces of content that still count as flagged after local overrides.
 *
 * Per-rule rows win when any remain. Once they are all overridden, fall back to issue counts
 * with those content ids removed — otherwise the KPI would keep the pre-override total.
 */
function flaggedCountAfterOverrides(
  result: ScreeningResultsResponse | undefined,
  resolvedFlags: readonly ScreeningFlag[] | undefined,
  overridden: ReadonlySet<string>,
  fallback: number,
): number {
  if (resolvedFlags?.length) {
    return countDistinct(resolvedFlags);
  }
  if (!result || overridden.size === 0) {
    return fallback;
  }
  return result.content.filter(
    (item) => (item.reviewResults?.issueFoundCount ?? 0) > 0 && !overridden.has(item.id),
  ).length;
}

function creatorName(inquiry: ScreeningInquiryResponseDto): string {
  const first = inquiry.creator?.firstName?.trim() ?? '';
  const last = inquiry.creator?.lastName?.trim() ?? '';
  const full = `${first} ${last}`.trim();
  if (full) {
    return full;
  }
  const handle = inquiry.creator?.instaHandle ?? inquiry.creator?.tikTokHandle;
  return handle ? `@${handle}` : 'Unknown creator';
}

function initialsOf(name: string): string {
  return name
    .replace(/^@/, '')
    .split(/\s+/)
    .map((word) => word.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function followerCount(inquiry: ScreeningInquiryResponseDto): number | undefined {
  const insta = inquiry.creator?.instaFollowers ?? 0;
  const tiktok = inquiry.creator?.tikTokFollowers ?? 0;
  const total = Math.max(insta, tiktok);
  return total > 0 ? total : undefined;
}
